import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  Link,
  SvgIcon,
  SvgIconProps,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { InformationBanner } from 'src/components/molecules';
import { FormState } from 'src/types';
import { InvestigationPlanModalProps, InvestigationStatus } from './types';
import { AiGeneratedBadge } from './AiGeneratedBadge';

const AI_DISCLAIMER =
  'This plan is AI-generated and may contain inaccuracies. Review all steps, questions, and findings before acting on them.';

const GENERATION_MESSAGES = [
  'Reading case summary and timeline...',
  'Identifying named parties and roles...',
  'Cross-referencing relevant company policies...',
  'Assessing severity and likely categories...',
  'Drafting investigation steps...',
  'Preparing interview questions for named parties...',
  'Preparing reporter interview questions...',
  'Compiling policy findings...',
  'Reviewing for anonymity constraints...',
  'Finalising investigation plan...',
];
const MESSAGE_INTERVAL_MS = 4000;

// Lucide "book-open" — outline icon matching the design.
const MenuBookIcon: React.FC<SvgIconProps> = (props) => (
  <SvgIcon
    {...props}
    viewBox="0 0 24 24"
    sx={{
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 2,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      ...props.sx,
    }}
  >
    <path d="M12 7v14" />
    <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" />
  </SvgIcon>
);

// Lucide "brain" — outline icon matching the design.
const PsychologyIcon: React.FC<SvgIconProps> = (props) => (
  <SvgIcon
    {...props}
    viewBox="0 0 24 24"
    sx={{
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 2,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      ...props.sx,
    }}
  >
    <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
    <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" />
    <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4" />
    <path d="M17.599 6.5a3 3 0 0 0 .399-1.375" />
    <path d="M6.003 5.125A3 3 0 0 0 6.401 6.5" />
    <path d="M3.477 10.896a4 4 0 0 1 .585-.396" />
    <path d="M19.938 10.5a4 4 0 0 1 .585.396" />
    <path d="M6 18a4 4 0 0 1-1.967-.516" />
    <path d="M19.967 17.484A4 4 0 0 1 18 18" />
  </SvgIcon>
);

export const InvestigationPlanModal: React.FC<InvestigationPlanModalProps> = ({
  open,
  caseDisplayId,
  plan,
  onClose,
  onAccept,
  onEditPlan,
}) => {
  const [showFindings, setShowFindings] = useState(false);
  const [visibleMessages, setVisibleMessages] = useState<string[]>([]);
  const messageIndexRef = useRef(0);

  const isGenerating = plan?.status === InvestigationStatus.GENERATING;
  const isFailed = plan?.status === InvestigationStatus.FAILED;
  const isAccepted = plan?.status === InvestigationStatus.ACCEPTED;

  useEffect(() => {
    if (!isGenerating) {
      setVisibleMessages([]);
      messageIndexRef.current = 0;
      return;
    }
    setVisibleMessages([GENERATION_MESSAGES[0]!]);
    messageIndexRef.current = 1;
    const interval = setInterval(() => {
      const next = messageIndexRef.current;
      if (next < GENERATION_MESSAGES.length) {
        setVisibleMessages((prev) => [...prev, GENERATION_MESSAGES[next]!]);
        messageIndexRef.current = next + 1;
      }
    }, MESSAGE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isGenerating]);

  const handleClose = () => {
    setShowFindings(false);
    onClose();
  };

  if (!plan) return null;

  const findings = plan.policyFindings;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={false}
      sx={{
        zIndex: 2100,
        '& .MuiDialog-container': {
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '90vh',
          width: '100%',
          maxWidth: '1200px',
          borderRadius: '12px',
          m: 'auto',
        },
      }}
    >
      <Box sx={{ pt: 4, px: 4, pb: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 1 }}
        >
          <Box>
            <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 1 }}>
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{ fontSize: '1.375rem' }}
              >
                Proposed Investigation Strategy: Case {caseDisplayId}
              </Typography>
              <AiGeneratedBadge data-testid="investigation-plan-ai-badge" />
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '0.875rem' }}
            >
              AI-generated plan based on case details, company policies, and
              investigation best practices.
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            aria-label="Close investigation plan"
            data-testid="investigation-plan-modal-close"
            sx={{ mt: -0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ px: 4, pt: 4, pb: 4 }}>
        {!isGenerating && (
          <Box sx={{ mb: 3 }}>
            <InformationBanner
              state={FormState.INFO}
              description={AI_DISCLAIMER}
              $fullWidth
              data-testid="investigation-plan-ai-disclaimer"
            />
          </Box>
        )}
        {isGenerating ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            sx={{ py: 8 }}
          >
            <Box sx={{ width: '100%', maxWidth: 480 }}>
              {visibleMessages.map((msg, i) => (
                <Typography
                  key={i}
                  variant="body2"
                  color={
                    i === visibleMessages.length - 1
                      ? 'text.primary'
                      : 'text.disabled'
                  }
                  sx={{
                    mb: 1,
                    transition: 'color 0.4s',
                    fontWeight: i === visibleMessages.length - 1 ? 500 : 400,
                  }}
                >
                  {msg}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : isFailed ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            sx={{ py: 8 }}
            data-testid="investigation-plan-failed-state"
          >
            <Typography variant="body1" color="error" sx={{ mb: 2 }}>
              Plan generation failed
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3, textAlign: 'center' }}
            >
              Something went wrong while generating the investigation plan.
            </Typography>
            <Button
              variant="outlined"
              onClick={onEditPlan}
              data-testid="investigation-plan-retry-button"
            >
              Try Again
            </Button>
          </Box>
        ) : showFindings && findings ? (
          <>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => setShowFindings(false)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                mb: 5,
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              <ArrowBackIcon sx={{ mr: 0.75, fontSize: '18px' }} />
              Back to Strategy Plan
            </Link>

            <Box display="flex" gap={6}>
              {/* Left: Flagged Policy Excerpts */}
              <Box flex={1}>
                <Box display="flex" alignItems="center" sx={{ mb: 3 }}>
                  <MenuBookIcon
                    sx={{ mr: 1, fontSize: '18px', color: 'text.secondary' }}
                  />
                  <Typography
                    variant="overline"
                    fontWeight={600}
                    color="text.secondary"
                    sx={{ fontSize: '0.75rem', letterSpacing: 0.5 }}
                  >
                    Flagged Policy Excerpts
                  </Typography>
                </Box>
                {findings.excerpts.map((excerpt) => (
                  <Box
                    key={excerpt.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '8px',
                      p: 2,
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      {excerpt.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.5, fontStyle: 'italic' }}
                    >
                      &quot;{excerpt.excerpt}&quot;
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Right: AI Analysis & Case Implications */}
              <Box flex={1}>
                <Box display="flex" alignItems="center" sx={{ mb: 3 }}>
                  <PsychologyIcon
                    sx={{ mr: 1, fontSize: '18px', color: 'text.secondary' }}
                  />
                  <Typography
                    variant="overline"
                    fontWeight={600}
                    color="text.secondary"
                    sx={{ fontSize: '0.75rem', letterSpacing: 0.5 }}
                  >
                    AI Analysis & Case Implications
                  </Typography>
                </Box>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px',
                    backgroundColor: 'action.hover',
                    p: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ lineHeight: 1.6, color: 'text.primary' }}
                  >
                    {findings.analysis}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          <>
            {/* Column Headers */}
            <Box display="flex" gap={4} sx={{ mb: 2 }}>
              <Box flex={1}>
                <Typography
                  variant="overline"
                  fontWeight={600}
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    fontSize: '0.75rem',
                    letterSpacing: 0.5,
                  }}
                >
                  Investigation & Mitigation Plan
                </Typography>
              </Box>
              <Box flex={1}>
                <Typography
                  variant="overline"
                  fontWeight={600}
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    fontSize: '0.75rem',
                    letterSpacing: 0.5,
                  }}
                >
                  Targeted Questions & Data Requirements
                </Typography>
              </Box>
            </Box>

            {/* Column Content */}
            <Box display="flex" gap={4}>
              {/* Left Column: Investigation Steps */}
              <Box flex={1}>
                {plan.steps.map((step) => (
                  <Box
                    key={step.id}
                    display="flex"
                    alignItems="flex-start"
                    sx={{ mb: 3 }}
                  >
                    <CheckCircleOutlineIcon
                      color="primary"
                      sx={{ mr: 1.5, mt: 0.25, fontSize: '20px' }}
                    />
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ mb: 0.5 }}
                      >
                        {step.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.5 }}
                      >
                        {step.description}
                      </Typography>
                      {step.hasSpecialAction && (
                        <Link
                          href="#"
                          variant="body2"
                          sx={{
                            mt: 0.75,
                            display: 'inline-flex',
                            alignItems: 'center',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            if (findings) setShowFindings(true);
                          }}
                        >
                          <DescriptionIcon sx={{ mr: 0.5, fontSize: '16px' }} />
                          View Findings
                        </Link>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Right Column: Targeted Questions */}
              <Box flex={1}>
                {plan.targetedQuestions.map((group) => {
                  const stakeholderType = group.stakeholderType.replace(
                    /_/g,
                    ' ',
                  );
                  return (
                    <Box key={group.id} sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: '8px',
                          p: 2,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          sx={{
                            mb: 1.5,
                            fontSize: '0.75rem',
                            letterSpacing: 0.5,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{ fontWeight: 700, textTransform: 'uppercase' }}
                          >
                            {stakeholderType}
                          </Box>
                          {group.stakeholderName && (
                            <Box component="span" sx={{ fontWeight: 400 }}>
                              {` - ${group.stakeholderName}`}
                            </Box>
                          )}
                        </Typography>
                        {group.questions.map((question, idx) => (
                          <Typography
                            key={idx}
                            variant="body2"
                            sx={{
                              lineHeight: 1.5,
                              minHeight: '3rem',
                              mb: idx === group.questions.length - 1 ? 0 : 1.5,
                            }}
                          >
                            {question}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      {!isGenerating && !isFailed && !showFindings && (
        <>
          <Divider />
          <DialogActions
            sx={{ px: 3, py: 2, justifyContent: 'flex-end', gap: 1.5 }}
          >
            <Button
              variant="outlined"
              onClick={onEditPlan}
              disabled={isAccepted}
            >
              Edit Plan
            </Button>
            <Button
              variant="contained"
              onClick={onAccept}
              disabled={isAccepted}
              sx={{ minWidth: 140 }}
            >
              Accept & Initiate
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
